import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { CanDeactivateComponent } from '@app/interfaces/can-deactivate-component.interface';
import { ATSJobIntegration } from '@app/models/company/ats-job-integration.model';
import { Employee } from '@app/models/employee/employee.model';
import { JobAttachment } from '@app/models/recruiting/job-attachments.model';
import { JobPostingLocationType } from '@app/models/recruiting/job-posting-location-type.model';
import { ManagerAssignment } from '@app/models/recruiting/manager-assignment.model';
import { Question } from '@app/models/recruiting/question.model';
import { JobAttachmentTypes } from '@app/modules/applicant-tracker/enums/job-attachment-types.enum';
import {
    JobPostingStoreAttachments,
    JobPostingStoreHelpersForm,
} from '@app/modules/forms/applicant-tracker/components';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { JobPostingLocationTypes } from '@applicant-tracker/constants/job-posting-location-types.constant';
import { ViolationItem } from '@applicant-tracker/interfaces/validation-response.interface';
import {
    HiringPermissionsService,
    HiringTeamAssignmentService,
    IndeedValidationService,
    JobPostingQuestionsService,
} from '@applicant-tracker/services';
import { StoreView } from '@common/views';
import { Company } from '@models/company/company.model';
import { JobBoard } from '@models/recruiting/job-board.model';
import { JobPosting } from '@models/recruiting/job-posting.model';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import onChange, { ApplyData } from 'on-change';
import { Subscription } from 'rxjs';
import { ActiveIntegrations } from '@settings/enums/integration.enum';

@Component({
    templateUrl: './job-posting-store.template.html',
    styleUrls: ['./job-posting-store.style.scss'],
})
export class JobPostingStoreView extends StoreView implements OnInit, OnDestroy, CanDeactivateComponent {
    @ViewChild('attachmentsForm') jobPostingAttachmentForm: JobPostingStoreAttachments;
    @ViewChild(JobPostingStoreHelpersForm) storeHelpersComponent!: JobPostingStoreHelpersForm;

    jobPosting: JobPosting;
    jobIntegrations: ATSJobIntegration[] = [];
    autoArchivingQuestions: Question[] = [];
    additionalQuestions: Question[] = [];
    hiringTeamMembers = [this.auth.employee];
    jobAttachments = [
        new JobAttachment({
            name: this.translateService.instant('applicant-tracker.resume'),
            isRequired: true,
            jobAttachmentTypeId: JobAttachmentTypes['File'],
        }),
        new JobAttachment({
            name: this.translateService.instant('applicant-tracker.coverLetter'),
            isRequired: true,
            jobAttachmentTypeId: JobAttachmentTypes['File'],
        }),
    ];
    subscription: Subscription;
    jobBoard: JobBoard;
    indeedValidationMessages: ViolationItem[];

    jobPostingIdToDuplicate: string | null = null;
    routeToRedirectTo = '/recruiting/jobPostings';
    notFilled = '----------';
    jobPostingFormTitle = 'applicant-tracker.newJobPosting';

    isLoading = true;
    draftSaved = true;
    isWarningDialogOpen = false;
    canNavigateAway = false;
    isValidationModalOpen = false;
    isUnsavedDialogOpen = false;
    doNotPostToIndeed = false;

    hasQuickApply = false;
    hasConsentFlag = false;

    rawLocationTypes = [];
    hybridType = null;

    constructor(
        private notify: NotifyService,
        private router: Router,
        private auth: AuthService,
        private route: ActivatedRoute,
        private hiringPermissionsService: HiringPermissionsService,
        private hiringTeamAssignmentService: HiringTeamAssignmentService,
        private analyticService: AnalyticService,
        private featureService: FeatureService,
        private indeedValidationService: IndeedValidationService,
        private readonly jobPostingQuestionsService: JobPostingQuestionsService,
        private readonly translateService: TranslateService
    ) {
        super();

        this.subscription = this.router.events.subscribe((e) => {
            if (e instanceof NavigationStart) {
                this.routeToRedirectTo = e.url;
            }
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    warn($event: BeforeUnloadEvent): void {
        $event.returnValue = 'Your job posting will not be saved. Are you sure you want to exit?';
    }

    async ngOnInit(): Promise<void> {
        try {
            await this.getJobBoard();

            if (!this.jobBoard || !this.jobBoard.companySlug) {
                this.canNavigateAway = true;
                this.router.navigate(['/', 'recruiting', 'jobPostings']);
            }

            await this.hiringPermissionsService.handle();

            const [rawLocationTypes] = await JobPostingLocationType.all();
            this.rawLocationTypes = rawLocationTypes;
            this.hybridType = this.rawLocationTypes.find((item) => item.name === JobPostingLocationTypes.hybrid);

            if (this.route?.snapshot.queryParams.jobPostingId) {
                this.jobPostingIdToDuplicate = this.route.snapshot.queryParams.jobPostingId;
                await this.duplicateJobPosting(this.jobPostingIdToDuplicate);
                this.jobPostingFormTitle = this.getJobPostingFormTitle();
                return;
            }

            this.jobPosting = new JobPosting();
            this.jobPosting.allLocationTypes = this.rawLocationTypes;

            // Call this before change detection
            this.jobPosting.hiringTeamAssign = this.hiringTeamMembers;

            this.setupOnChangeDetection();

            this.hasQuickApply = (
                await Company.with('indeedIntegration.integration').find(this.auth.company.id)
            )?.indeedIntegration?.isQuickApplyEnabled;
            this.hasConsentFlag = await this.featureService.has(FeatureFlag.atsApplicantConsent);

            this.jobPostingFormTitle = this.getJobPostingFormTitle();
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    canDeactivate(): boolean {
        if (!this.canNavigateAway) {
            if (this.showDraftButton()) {
                this.isUnsavedDialogOpen = true;
                return false;
            }

            this.isWarningDialogOpen = true;
        }

        return this.canNavigateAway;
    }

    changeDetection = (
        path: string,
        value: string | number | ATSJobIntegration | JobAttachment | Question,
        previousValue: string | number | ATSJobIntegration | JobAttachment | Question
    ): void => {
        if (!path) {
            return;
        }

        if (value !== previousValue) {
            this.draftSaved = false;
            this.canNavigateAway = false;
        }
    };

    setupOnChangeDetection(): void {
        this.jobPosting = onChange(
            this.jobPosting,
            (
                path: string,
                value: string | number | JobPosting,
                previousValue: string | number | JobPosting,
                applyData: ApplyData
            ) => {
                if (applyData?.name === 'attach' || applyData?.name === 'detach') {
                    this.draftSaved = false;
                    this.canNavigateAway = false;
                }

                // We don't want the auto-score keywords to trigger a change.
                /* Will get an object (after the request completes) we don't
                want to count that as a change since a specific value has not been changed yet */
                if (value instanceof JobPosting) {
                    return;
                }

                if (value !== previousValue && !['autoScoreKeywords'].includes(path)) {
                    this.draftSaved = false;
                    this.canNavigateAway = false;
                }
            }
        );

        this.jobAttachments = onChange(this.jobAttachments, this.changeDetection);

        this.additionalQuestions = onChange(this.additionalQuestions, this.changeDetection);

        this.autoArchivingQuestions = onChange(this.autoArchivingQuestions, this.changeDetection);

        this.jobIntegrations = onChange(this.jobIntegrations, this.changeDetection);

        this.jobPosting.allLocationTypes = this.rawLocationTypes;
    }

    onBack(): void {
        this.isWarningDialogOpen = true;
    }

    getJobPostingFormTitle(): string {
        if (
            this.jobPostingIdToDuplicate &&
            (this.jobPosting?.draftAt === null || this.jobPosting?.draftAt === undefined)
        ) {
            return 'applicant-tracker.duplicateJobPosting';
        }

        return 'applicant-tracker.newJobPosting';
    }

    closeWarningDialog(): void {
        this.isWarningDialogOpen = false;
    }

    closeUnsavedDialog(): void {
        this.isUnsavedDialogOpen = false;

        this.jobPostingIdToDuplicate && !this.jobPosting.draftAt
            ? this.analyticService.trackEvent(
                  AnalyticEvents.RecruitingDuplicateJobPostingUnsavedChangesClickDiscardChanges
              )
            : this.analyticService.trackEvent(
                  AnalyticEvents.RecruitingCreateJobPostingUnsavedChangesClickDiscardChanges
              );
    }

    goBack(): void {
        this.canNavigateAway = true;

        if (this.routeToRedirectTo) {
            this.router.navigateByUrl(this.routeToRedirectTo);
            return;
        }

        this.router.navigate(['recruiting', 'jobPostings']);
    }

    showDraftButton(): boolean {
        // If all of these buttons fields are filled then show the save draft button.
        if (this.jobPosting.title && this.jobPosting.level && this.jobPosting.category) {
            return true;
        }
        // The save draft button is not shown then the user can navigate away with no prompt since they have no changes.
        return false;
    }

    async onSaveDraft(navigateAway = false): Promise<void> {
        if (this.draftSaved) {
            return;
        }

        this.isLoading = true;

        const draftAtBefore = this.jobPosting.draftAt;

        this.jobPosting.draftAt = new Date();
        this.closeUnsavedDialog();

        // We fill these fields by default so the job posting table will not have empty columns.
        this.jobPosting.city = this.setValueOnEmptyFields(this.jobPosting.city);
        this.jobPosting.region = this.setValueOnEmptyFields(this.jobPosting.region);
        this.jobPosting.country = this.setValueOnEmptyFields(this.jobPosting.country);

        try {
            this.jobPosting = await this.jobPosting.save();
            //so that applicantOptionalFields is fresh
            const freshJobPosting: JobPosting = await JobPosting.param('company', this.auth.company.id)
                .with(['applicantOptionalFields'])
                .find(this.jobPosting.id);
            this.jobPosting.applicantOptionalFields = freshJobPosting.applicantOptionalFields;
            this.jobPosting.allLocationTypes = this.rawLocationTypes;

            // Gets all of the currently assigned members of the hiring team
            const hiringTeam = this.getHiringTeam();

            const detach = hiringTeam.filter((hiringTeamMember: Employee) => {
                return !this.jobPosting.hiringTeamAssign.find((employee: Employee) => {
                    return hiringTeamMember.id === employee.id;
                });
            });

            await this.hiringTeamAssignmentService.assign(
                this.jobPosting.id,
                this.hiringTeamAssignmentService.createAttach(this.jobPosting.hiringTeamAssign),
                this.hiringTeamAssignmentService.createDetach(detach)
            );

            this.jobPosting.city = this.clearNotFilled(this.jobPosting.city);
            this.jobPosting.region = this.clearNotFilled(this.jobPosting.region);
            this.jobPosting.country = this.clearNotFilled(this.jobPosting.country);

            await this.saveJobAttachments();

            await this.jobPostingQuestionsService.save(this.jobPosting, [
                ...this.additionalQuestions,
                ...this.autoArchivingQuestions,
            ]);

            await this.refreshQuestions();

            this.additionalQuestions = onChange(this.additionalQuestions, this.changeDetection);

            this.autoArchivingQuestions = onChange(this.autoArchivingQuestions, this.changeDetection);

            this.draftSaved = true;
            this.canNavigateAway = true;

            this.storeHelpersComponent.refreshRequiredFields();

            if (!navigateAway && !this.isUnsavedDialogOpen) {
                this.jobPostingIdToDuplicate && !draftAtBefore
                    ? this.analyticService.trackEvent(AnalyticEvents.RecruitingDuplicateJobPostingAnyStepClickSaveDraft)
                    : this.analyticService.trackEvent(AnalyticEvents.RecruitingCreateJobPostingAnyStepClickSaveDraft);
            }

            if (navigateAway) {
                this.jobPostingIdToDuplicate && !draftAtBefore
                    ? this.analyticService.trackEvent(
                          AnalyticEvents.RecruitingDuplicateJobPostingUnsavedChangesClickSaveDraft
                      )
                    : this.analyticService.trackEvent(
                          AnalyticEvents.RecruitingCreateJobPostingUnsavedChangesClickSaveDraft
                      );

                this.router.navigate(['recruiting', 'jobPostings']);
            }
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    clearNotFilled(value: string): string {
        if (value === this.notFilled) {
            return '';
        }

        return value;
    }

    setValueOnEmptyFields(value: string): string {
        if (typeof value === 'undefined' || !value) {
            return this.notFilled;
        }

        return value;
    }

    async onSave(): Promise<void> {
        this.isLoading = true;

        // Validate job posting for posting to indeed.
        if (this.hasSelectedIndeedIntegration() && !this.doNotPostToIndeed) {
            try {
                const validation = await this.indeedValidationService.validateCreate(this.jobPosting);

                if (validation.failedCount !== 0) {
                    this.indeedValidationMessages = this.indeedValidationService.parseValidationMessages(validation);
                    this.isValidationModalOpen = true;
                    this.isLoading = false;

                    return;
                }
            } catch (err) {
                this.indeedValidationMessages = err.error.errors.map((e) => ({
                    field: 'Indeed Error',
                    message: e.detail,
                }));
                this.isValidationModalOpen = true;
                this.isLoading = false;

                return;
            }
        }

        // Remove draft at if exists
        if (this.jobPosting.draftAt) {
            this.jobPosting.draftAt = null;
        }

        this.jobPosting
            .param('company', this.auth.company.id)
            .save()
            .then(async (savedJobPosting: JobPosting) => {
                this.jobPostingIdToDuplicate
                    ? this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingMeatballCreateDuplicateSave)
                    : this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingSave);

                await this.saveJobIntegrations(savedJobPosting);
                await this.saveJobAttachments();
                await this.hiringTeamAssignmentService.assign(
                    savedJobPosting.id,
                    this.hiringTeamAssignmentService.createAttach(this.jobPosting.hiringTeamAssign)
                );

                try {
                    await this.jobPostingQuestionsService.save(savedJobPosting, [
                        ...this.autoArchivingQuestions,
                        ...this.additionalQuestions,
                    ]);

                    return;
                } catch (error) {
                    this.notify.error(error.message);
                } finally {
                    this.isLoading = false;
                    // Allow navigation when everything else is complete
                    this.canNavigateAway = true;
                    this.router.navigate(['/', 'recruiting', 'jobPostings']);
                    this.notify.success('applicant-tracker.jobPostingSaved');
                }
            })
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.couldNotSaveJobPosting');
                this.isLoading = false;
            });
    }

    async saveJobIntegrations(jobPosting: JobPosting): Promise<void> {
        if (!this.jobIntegrations.length) {
            return;
        }

        await Promise.all(
            this.jobIntegrations
                .filter((jobIntegration: ATSJobIntegration) => {
                    return !(
                        this.doNotPostToIndeed &&
                        jobIntegration.integrationType.integrationType.name === ActiveIntegrations.indeed
                    );
                })
                .map((jobIntegration: ATSJobIntegration) => {
                    jobIntegration.atsJobId = jobPosting.id;
                    return jobIntegration.save();
                })
        );
    }

    async saveJobAttachments(): Promise<void> {
        if (this.jobPostingAttachmentForm.jobAttachments.length) {
            const jobAttachments = this.jobPostingAttachmentForm.jobAttachments.map((jobAttachment: JobAttachment) => {
                jobAttachment.atsJobId = this.jobPosting.id;
                return jobAttachment;
            });
            // Need to make calls in sequential order to the order saved in the backend
            for (const jobAttachment of jobAttachments) {
                await jobAttachment.save();
            }
        }
    }

    onSaveWithoutIntegration(): void {
        this.isValidationModalOpen = false;
        this.doNotPostToIndeed = true;
        this.analyticService.trackEvent(AnalyticEvents.PostingJobToIndeedErrorDuringCreationOfJobPostingConfirm);
        this.onSave();
    }

    onClosingValidationModal(): void {
        this.isValidationModalOpen = false;
        this.analyticService.trackEvent(AnalyticEvents.PostingJobToIndeedErrorDuringCreationOfJobPostingCancel);
    }

    async refreshQuestions(): Promise<void> {
        const reloadedJobPosting = await JobPosting.param('company', this.auth.company.id)
            .with(['autoArchiveQuestions', 'additionalQuestions'])
            .find(this.jobPosting.id);

        this.additionalQuestions = reloadedJobPosting.atsAdditionalQuestions;
        this.autoArchivingQuestions = reloadedJobPosting.autoArchivingQuestions;
    }

    private getHiringTeam(): Employee[] {
        return this.jobPosting.managerAssignments.map((managerAssignment: ManagerAssignment) => {
            managerAssignment.employee.accessLevelId = managerAssignment.accessLevelId;
            return managerAssignment.employee;
        });
    }

    private async duplicateJobPosting(jobPostingIdToDuplicate: string): Promise<void> {
        this.isLoading = true;

        const jobPostingToDuplicate: JobPosting = await JobPosting.param('company', this.auth.company.id)
            .with([
                'autoArchiveQuestions',
                'additionalQuestions',
                'jobAttachments',
                'applicantOptionalFields',
                'hiringTeam',
                'managerAssignments.employee',
            ])
            .find(jobPostingIdToDuplicate);

        this.jobPosting = jobPostingToDuplicate;

        // Set title to empty string and clone the model (removes the id)
        if (!this.jobPosting.draftAt) {
            // Clone job posting
            this.jobPosting = jobPostingToDuplicate.clone();
            this.jobPosting.title = '';

            this.additionalQuestions =
                this.jobPosting.atsAdditionalQuestions?.map((question) => question.clone()) || [];
            this.autoArchivingQuestions =
                this.jobPosting.autoArchivingQuestions?.map((question) => question.clone()) || [];

            // Copy attachments
            this.jobPosting.jobAttachments =
                this.jobPosting?.jobAttachments?.map((jobAttachment) => jobAttachment.clone()) || [];
        }

        if (this.jobPosting.draftAt) {
            this.additionalQuestions = this.jobPosting.atsAdditionalQuestions;
            this.autoArchivingQuestions = this.jobPosting.autoArchivingQuestions;

            this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingEditDraft);

            this.jobPosting.city = this.clearNotFilled(this.jobPosting.city);
            this.jobPosting.region = this.clearNotFilled(this.jobPosting.region);
            this.jobPosting.country = this.clearNotFilled(this.jobPosting.country);
        }

        // use clone deep since we don't want the references to be the same
        this.jobPosting.hiringTeamAssign = this.jobPosting.managerAssignments.length
            ? cloneDeep(this.getHiringTeam())
            : [this.auth.employee];

        this.setupOnChangeDetection();
        this.canNavigateAway = this.showDraftButton();
        this.isLoading = false;

        this.jobPosting.allLocationTypes = this.rawLocationTypes;
    }

    private async getJobBoard(): Promise<void> {
        const [[jobBoard]] = await JobBoard.get();
        this.jobBoard = jobBoard;
    }

    private hasSelectedIndeedIntegration(): boolean {
        return Boolean(
            this.jobIntegrations.find((jobIntegration) => {
                return jobIntegration.integrationType.integrationType.name === ActiveIntegrations.indeed;
            })
        );
    }
}
